import React, { Suspense } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { isMobile } from 'react-device-detect';
import {
    Container,
    Grid,
    IconButton,
    MenuItem,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    Tab,
    Tabs,
} from '@mui/material';

import { Add, AddCard, Receipt } from '@mui/icons-material';
import { a11yProps, TabPanels } from 'components/mui/Tabs';
import useContextMenu from 'hooks/useContextMenu';
import useSwiperTabs from 'hooks/useSwiperTabs';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import { InvoiceStatus } from 'models/InvoiceModel';
import { CompanyType } from 'models/CompanyModel';
import useInvoiceModals from 'modules/invoices/hooks/useInvoiceModals';
import ForShipperTable from 'modules/invoices/views/ForShipperTable';
import ForShipperList from 'modules/invoices/views/ForShipperList';
import FobUnpaidTable from 'modules/invoices/views/FobUnpaidTable';
import FobUnpaidList from 'modules/invoices/views/FobUnpaidList';
import FobPaidTable from 'modules/invoices/views/FobPaidTable';
import FobPaidList from 'modules/invoices/views/FobPaidList';
import auth from 'recoil/atoms/auth';


const tabs = [
    'unpaid',
    'paid',
    'fob-unpaid',
    'fob-paid',
];

const InvoicesInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [status, setStatus] = useQueryParam('status', withDefault(StringParam, 'unpaid'));
    const {index, setIndex, setSwiper} = useSwiperTabs(tabs.indexOf(status));
    const modals = useInvoiceModals();

    const change = (idx: number, fromSwiper?: boolean) => {
        setIndex(idx, fromSwiper);
        setStatus(tabs[idx]);
    };

    useDidUpdateEffect(() => setIndex(tabs.indexOf(status)), [status]);

    const {openMenu} = useContextMenu(({close}) => [
        <MenuItem
            key="top_up"
            onClick={() => {
                modals.openTopUpInvoiceModal().then(data => navigate(`/invoice/${data.invoice_id}`));
                close();
            }}
        >
            {t(`invoice_top_up`)}
        </MenuItem>,
        <MenuItem
            key="simple"
            onClick={() => {
                modals.openSimpleInvoiceModal().then(data => navigate(`/invoice/${data.invoice_id}`));
                close();
            }}
        >
            {t(`invoice_simple`)}
        </MenuItem>,
    ]);

    const id = 'invoices';

    const tabButtons = (
        <Tabs
            variant="scrollable"
            value={index}
            onChange={(e, v) => change(v)}
            aria-label="Invoices tabs"
        >
            {tabs.map((t, i) => {
                if (my.type === CompanyType.Shipper && t.startsWith('fob'))
                    return null;

                const label = t.replace('-', ' ');
                return <Tab key={t} label={label} {...a11yProps(id, i)} />
            })}
        </Tabs>
    );

    return (
        <Container maxWidth={false} sx={{mt: 2}}>
            {isMobile ? tabButtons : (
                <Grid container justifyContent="space-between">
                    <Grid item>
                        {tabButtons}
                    </Grid>
                    <Grid item>
                        {my.permissions.has('invoices create') && (
                            <IconButton
                                onClick={openMenu}
                            >
                                <Add/>
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            )}

            <TabPanels
                active={index}
                id={id}
                setIndex={change}
                setSwiper={setSwiper}
                narrow
                tabs={isMobile ? [
                    <ForShipperList status={InvoiceStatus.Unpaid}/>,
                    <ForShipperList status={InvoiceStatus.Paid}/>,
                    <FobUnpaidList/>,
                    <FobPaidList/>,
                ] : [
                    <ForShipperTable status={InvoiceStatus.Unpaid}/>,
                    <ForShipperTable status={InvoiceStatus.Paid}/>,
                    <FobUnpaidTable/>,
                    <FobPaidTable/>,
                ]}
            />

            {isMobile && (
                <SpeedDial
                    ariaLabel="Create invoices"
                    sx={{position: 'absolute', bottom: 16, right: 16}}
                    icon={<SpeedDialIcon/>}
                >
                    <SpeedDialAction
                        icon={<Receipt/>}
                        tooltipTitle={t(`invoice_simple`)}
                        tooltipOpen
                        onClick={() => modals.openSimpleInvoiceModal().then(data => navigate(`/invoice/${data.invoice_id}`))}
                    />

                    <SpeedDialAction
                        icon={<AddCard/>}
                        tooltipTitle={t(`invoice_top_up`)}
                        tooltipOpen
                        onClick={() => modals.openTopUpInvoiceModal().then(data => navigate(`/invoice/${data.invoice_id}`))}
                    />
                </SpeedDial>
            )}
        </Container>
    );
};

const Invoices = () => {
    return (
        <Suspense fallback={null}>
            <InvoicesInner/>
        </Suspense>
    )
}

export default Invoices;
